// src/modules/app-views/products/pages/Document/tabs/FiscalTab/FiscalRatesCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect } from 'react';
import { Card, Col, Row, Divider, Form, Tag } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import translateX from 'utils/translateX';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import useZustand from 'hooks/useZustand';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { getProductTaxDefaults, getCrtCode } from 'utils/fiscalDefaults';
import { CRT_TAG_CONFIG } from 'services/fiscalService';
import {
  ICMS_CST_OPTIONS,
  ICMS_CSOSN_OPTIONS,
  PIS_CST_OPTIONS,
  COFINS_CST_OPTIONS,
  IPI_CST_OPTIONS,
} from 'modules/app-views/products/constants';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FiscalRatesCard = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedSubscription = useZustand('auth', 'subscription');
  const { form } = useSimpleFormContext() || {};
  const { data } = useDocumentContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS
  // ─────────────────────────────────────────────────────────────────────────────
  // Watch hasSt to conditionally show ST-related fields
  const hasSt = Form.useWatch(['fiscalData', 'hasSt'], form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  // Get company's tax regime to determine ICMS CST or CSOSN
  const taxRegime = storedSubscription?.fiscalData?.taxRegime;
  const fiscalData = storedSubscription?.fiscalData;
  const isSimples = taxRegime === 'simples_nacional' || taxRegime === 'simples_nacional_excesso';
  const icmsOptions = isSimples ? ICMS_CSOSN_OPTIONS : ICMS_CST_OPTIONS;
  const icmsLabel = isSimples ? 'icms_csosn' : 'icms_cst';
  const icmsFieldName = isSimples ? 'fiscalData.icms.csosn' : 'fiscalData.icms.cst';
  const crtCode = getCrtCode(taxRegime);
  const crtConfig = CRT_TAG_CONFIG[crtCode];

  // Pre-fill tax rate defaults for new documents based on company CRT
  useEffect(() => {
    const isNew = !data?._id || data._id === 'add';
    if (!isNew || !form || !taxRegime) return;

    form.setFieldsValue(getProductTaxDefaults(taxRegime, fiscalData));
  }, [data?._id, form, taxRegime, fiscalData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Card
      title={
        <div className="text-muted" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {translateX('tax_rates')}
          {crtConfig && (
            <Tag color={crtConfig.color} style={{ marginLeft: 4, fontSize: 11 }}>
              {crtConfig.label} - {crtConfig.description}
            </Tag>
          )}
        </div>
      }
      style={{ marginTop: 16 }}
    >
      {/* Row 1: CST Codes */}
      <Row gutter={ROW_GUTTER}>
        {/* ICMS CST/CSOSN */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleSelect
            name={icmsFieldName}
            label={icmsLabel}
            options={icmsOptions}
            allowClear
            tooltip="product_tax_tooltip"
          />
        </Col>

        {/* PIS CST */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleSelect
            name="fiscalData.pis.cst"
            label="pis_cst"
            options={PIS_CST_OPTIONS}
            allowClear
            tooltip="product_tax_tooltip"
          />
        </Col>

        {/* COFINS CST */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleSelect
            name="fiscalData.cofins.cst"
            label="cofins_cst"
            options={COFINS_CST_OPTIONS}
            allowClear
            tooltip="product_tax_tooltip"
          />
        </Col>

        {/* IPI CST */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleSelect
            name="fiscalData.ipi.cst"
            label="ipi_cst"
            options={IPI_CST_OPTIONS}
            allowClear
            tooltip="product_tax_tooltip"
          />
        </Col>
      </Row>

      <Divider style={{ margin: '16px 0' }} />

      {/* Row 2: Tax Rates */}
      <Row gutter={ROW_GUTTER}>
        {/* ICMS Rate */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleNumber
            name="fiscalData.icms.rate"
            label="icms_rate"
            min={0}
            max={100}
            decimalPlaces={2}
            percentage
            tooltip="product_tax_tooltip"
          />
        </Col>

        {/* PIS Rate */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleNumber
            name="fiscalData.pis.rate"
            label="pis_rate"
            min={0}
            max={100}
            decimalPlaces={2}
            percentage
            tooltip="product_tax_tooltip"
          />
        </Col>

        {/* COFINS Rate */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleNumber
            name="fiscalData.cofins.rate"
            label="cofins_rate"
            min={0}
            max={100}
            decimalPlaces={2}
            percentage
            tooltip="product_tax_tooltip"
          />
        </Col>

        {/* IPI Rate */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleNumber
            name="fiscalData.ipi.rate"
            label="ipi_rate"
            min={0}
            max={100}
            decimalPlaces={2}
            percentage
            tooltip="product_tax_tooltip"
          />
        </Col>
      </Row>

      {/* Row 3: ST Fields - Only shown when hasSt is true */}
      {hasSt && (
        <>
          <Divider style={{ margin: '16px 0' }} />

          <Row gutter={ROW_GUTTER}>
            {/* ICMS Base Reduction */}
            <Col xs={24} sm={12} lg={6}>
              <SimpleNumber
                name="fiscalData.icms.baseReduction"
                label="base_reduction"
                min={0}
                max={100}
                decimalPlaces={2}
                percentage
                tooltip="base_reduction_tooltip"
              />
            </Col>

            {/* MVA - Margem de Valor Agregado */}
            <Col xs={24} sm={12} lg={6}>
              <SimpleNumber
                name="fiscalData.icms.mva"
                label="mva"
                min={0}
                max={500}
                decimalPlaces={2}
                percentage
                tooltip="mva_tooltip"
              />
            </Col>

            {/* ST Rate */}
            <Col xs={24} sm={12} lg={6}>
              <SimpleNumber
                name="fiscalData.icms.stRate"
                label="st_rate"
                min={0}
                max={100}
                decimalPlaces={2}
                percentage
                tooltip="st_rate_tooltip"
              />
            </Col>
          </Row>
        </>
      )}
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FiscalRatesCard;
